import { useTranslation } from 'react-i18next';

import type { LayersQuery } from '@/graphql/graphql';

type LayerData = LayersQuery['layers'][number];

interface Props {
  /** A RAINFALL_TILE layer whose `metadata.rainfallSummary` carries the
   *  log-scale ramp anchors (min/max/p2/p50/p98). One legend per
   *  visible heatmap layer — typical case is a single layer at a time,
   *  but multi-run views still get distinct legends. */
  tileLayer: LayerData;
}

interface Summary {
  min?: number;
  max?: number;
  p2?: number;
  p50?: number;
  p98?: number;
}

/**
 * Tiny vertical legend pinned next to the rainfall control panels.
 * Renders the same blue ramp the worker bakes into the tile pyramid
 * (`gdaldem color-relief` with anchors at p2 / p50/2 / p50 / p98) so
 * the user can read upstream-area values off the heatmap.
 *
 * The colors are duplicated from worker.py's `_bake_log_rgba`; if the
 * ramp ever changes server-side, update both.
 */
export function RainfallHeatmapLegend({ tileLayer }: Props) {
  const { t } = useTranslation('project');
  const summary = ((tileLayer.metadata ?? {}) as { rainfallSummary?: Summary })
    .rainfallSummary;
  if (!summary || summary.p2 == null || summary.p98 == null) return null;

  // Convert log10 anchors back to readable upstream-area numbers.
  // Worker logs everything ≥ 1 m² (log10(1)=0 floor), so anchors
  // are guaranteed ≥ 0.
  const lower = Math.pow(10, summary.p2);
  const upper = Math.pow(10, summary.p98);
  const median = summary.p50 != null ? Math.pow(10, summary.p50) : null;

  return (
    <div className='pointer-events-auto rounded-md border border-slate-200/70 bg-white/90 px-2 py-2 text-[11px] shadow-sm backdrop-blur-md dark:border-stone-700/70 dark:bg-stone-900/90'>
      <div className='mb-1 flex items-center gap-1.5'>
        <span className='font-medium text-slate-900 dark:text-white'>
          {t('rainfallHeatmap.legendTitle')}
        </span>
      </div>
      <div className='flex items-stretch gap-2'>
        {/* The gradient bar — vertical, deepest blue at top (= max
            upstream), pale at bottom (= just-detected channels). The
            stops match the colorramp.txt the worker writes; if you
            tweak one, tweak the other. */}
        <div
          aria-hidden
          className='w-3 rounded-sm border border-slate-300/70 dark:border-stone-700/70'
          style={{
            height: 96,
            background:
              'linear-gradient(to bottom,' +
              ' rgb(8 48 107) 0%,' +
              ' rgb(66 146 198) 33%,' +
              ' rgb(158 202 225) 67%,' +
              ' rgb(240 248 255) 100%)',
          }}
        />
        <dl className='flex flex-col justify-between text-slate-700 dark:text-stone-200'>
          <dd className='font-mono tabular-nums'>{fmtArea(upper)}</dd>
          {median != null && (
            <dd className='font-mono tabular-nums opacity-70'>{fmtArea(median)}</dd>
          )}
          <dd className='font-mono tabular-nums'>{fmtArea(lower)}</dd>
        </dl>
      </div>
      <div className='mt-1 text-slate-500 dark:text-stone-400'>
        {t('rainfallHeatmap.legendUnit')}
      </div>
    </div>
  );
}

const fmtArea = (m2: number): string => {
  if (!Number.isFinite(m2)) return '—';
  if (m2 >= 10_000) return `${(m2 / 10_000).toFixed(1)} ha`;
  if (m2 >= 1000) return `${(m2 / 1000).toFixed(1)} k m²`;
  if (m2 >= 100) return `${m2.toFixed(0)} m²`;
  return `${m2.toFixed(1)} m²`;
};
